import { connect } from 'react-redux';

import { PREFERRED_UNIT_SYSTEMS, PreferredUnitSystem } from '@frontend/api';
import {
  Box,
  FormControl,
  FormLabel,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  NumberInput,
  NumberInputField,
  HStack,
  Text,
  Tooltip,
  useDisclosure,
  GearIcon,
  Segment,
  SegmentControl,
} from '@frontend/components';

import { DEFAULT_UNITS_BY_PREFERRED_SYSTEM } from 'constants/unitOfMeasure';

import { cadViewer } from 'modules/common/viewer/cadViewer/data/actions';
import {
  getDimensionalPrecision,
  getRenderNativeColors,
} from 'modules/common/viewer/cadViewer/data/reducer';

import { useUnitSystemPreference } from '../addOns/hooks';
import ViewerButton from './ViewerButton';

const DISPLAY_OPTIONS_TITLE = 'Display options';

const connector = connect(
  (state) => ({
    dimensionalPrecision: getDimensionalPrecision(state),
    renderNativeColors: getRenderNativeColors(state),
  }),
  {
    receiveDisplayUnits: cadViewer.receiveDisplayUnits,
    receiveDimensionalPrecision: cadViewer.receiveDimensionalPrecision,
    setRenderNativeColors: cadViewer.setRenderNativeColors,
  },
);

const inferDisplayUnits = (preferredUnitSystem: PreferredUnitSystem) =>
  DEFAULT_UNITS_BY_PREFERRED_SYSTEM[preferredUnitSystem];

const DisplayOptionsPopover = ({
  dimensionalPrecision,
  renderNativeColors,
  receiveDisplayUnits,
  receiveDimensionalPrecision,
  setRenderNativeColors,
}) => {
  const { onOpen, onClose, isOpen } = useDisclosure();
  const { preferredUnitSystem, setPreferredUnitSystem } =
    useUnitSystemPreference();

  const handleUnitsChange = (value: PreferredUnitSystem) => {
    if (value !== preferredUnitSystem) {
      receiveDisplayUnits({
        displayUnits: inferDisplayUnits(value),
      });
      setPreferredUnitSystem(value);
    }
    if (value === PREFERRED_UNIT_SYSTEMS.METRIC && dimensionalPrecision > 4) {
      receiveDimensionalPrecision({ dimensionalPrecision: 4 });
    }
  };

  const handleColorsChange = (value: string | boolean) => {
    const next = value === 'true' || value === true;
    if (next !== renderNativeColors) {
      setRenderNativeColors({ renderNativeColors: next });
    }
  };

  const handlePrecisionChange = (valueAsString: string | number) => {
    const n =
      typeof valueAsString === 'number' ? valueAsString : Number(valueAsString);
    if (!Number.isFinite(n) || n === dimensionalPrecision) {
      return;
    }
    receiveDimensionalPrecision({ dimensionalPrecision: n });
  };

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      autoFocus
      size="sm"
      placement="auto"
    >
      <PopoverTrigger>
        <ViewerButton
          isActive={isOpen}
          aria-label={DISPLAY_OPTIONS_TITLE}
          position="relative"
        >
          <Tooltip label={DISPLAY_OPTIONS_TITLE} placement="bottom">
            <Box
              position="absolute"
              inset={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <GearIcon boxSize={3} />
            </Box>
          </Tooltip>
        </ViewerButton>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverHeader textTransform="none">
          {DISPLAY_OPTIONS_TITLE}
        </PopoverHeader>
        <PopoverBody>
          <FormControl mb={1.5}>
            <FormLabel>Units</FormLabel>
            <SegmentControl
              name="cad-viewer-display-units"
              size="sm"
              value={preferredUnitSystem}
              onChange={(value) =>
                handleUnitsChange(value as PreferredUnitSystem)
              }
              w="100%"
            >
              <Segment value={PREFERRED_UNIT_SYSTEMS.IMPERIAL}>
                Imperial
              </Segment>
              <Segment value={PREFERRED_UNIT_SYSTEMS.METRIC}>Metric</Segment>
            </SegmentControl>
          </FormControl>
          <FormControl mb={1.5}>
            <FormLabel>Colors</FormLabel>
            <SegmentControl
              name="cad-viewer-display-colors"
              size="sm"
              value={renderNativeColors ? 'true' : 'false'}
              onChange={handleColorsChange}
              w="100%"
            >
              <Segment value="false">Model</Segment>
              <Segment value="true">Default</Segment>
            </SegmentControl>
          </FormControl>
          <FormControl mb={1.5}>
            <FormLabel>Dimensional precision</FormLabel>
            <HStack isInline={true}>
              <Text fontSize="sm">Decimal places</Text>
              <NumberInput
                clampValueOnBlur
                focusInputOnChange
                max={
                  preferredUnitSystem === PREFERRED_UNIT_SYSTEMS.METRIC ? 4 : 6
                }
                min={1}
                step={1}
                value={dimensionalPrecision}
                onChange={handlePrecisionChange}
                pattern="[0-9]*(.[0-9]+)?"
                size="sm"
                width="auto"
              >
                <NumberInputField width={6} />
              </NumberInput>
            </HStack>
          </FormControl>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default connector(DisplayOptionsPopover);
